import React from 'react';
import { useSchedule } from '../context/ScheduleContext';
import { format } from 'date-fns';
import { Bell, Clock, ExternalLink, Facebook, Info, RefreshCw, Smartphone, Wifi } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const hasNotificationAPI = typeof Notification !== 'undefined';

export const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { settings, updateSettings, virtualTime } = useSchedule();
  const isDev = import.meta.env.DEV;

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value ? new Date(e.target.value) : null;
    updateSettings({ simulationTime: newTime });
  };

  const clearTime = () => {
    updateSettings({ simulationTime: null });
  };

  const notificationOptions = [5, 10, 15, 30];

  return (
    <div className="container mx-auto p-4 pb-24 space-y-8">
      <h1 className="text-3xl font-bold mb-6 text-primary">{t('settings.title')}</h1>

      {/* Language */}
      <section className="bg-surface p-6 rounded-lg border border-gray-800">
        <h2 className="text-xl font-bold mb-4">{t('settings.languageTitle')}</h2>
        <p className="text-sm text-gray-400 mb-4">{t('settings.languageDescription')}</p>
        <div className="grid grid-cols-2 gap-2">
          {(['en', 'es'] as const).map((lang) => (
            <button
              key={lang}
              onClick={() => updateSettings({ language: lang })}
              className={`p-3 rounded-lg border font-bold transition-all ${
                settings.language === lang
                  ? 'bg-primary text-white border-primary'
                  : 'bg-background text-gray-400 border-gray-700 hover:border-gray-500'
              }`}
            >
              {lang === 'en' ? t('settings.english') : t('settings.spanish')}
            </button>
          ))}
        </div>
      </section>

      {/* Time Simulation (dev only) */}
      {isDev && (
        <section className="bg-surface p-6 rounded-lg border border-gray-800">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-secondary" />
            {t('settings.timeSimulationTitle')}
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            {t('settings.timeSimulationDescription')}
          </p>
          
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-300">{t('settings.virtualTimeLabel')}</label>
              <input 
                type="datetime-local" 
                className="bg-background border border-gray-700 rounded p-2 text-white w-full"
                value={settings.simulationTime ? format(settings.simulationTime, "yyyy-MM-dd'T'HH:mm") : ''}
                onChange={handleTimeChange}
              />
            </div>

            <div className="flex items-center justify-between bg-background p-3 rounded border border-gray-800">
              <span className="text-sm text-gray-400">{t('settings.currentSystemTimeLabel')}</span>
              <span className="font-mono text-secondary">{format(virtualTime, 'HH:mm:ss')}</span>
            </div>

            <button 
              onClick={clearTime}
              className="w-full flex items-center justify-center gap-2 py-3 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              {t('settings.resetToRealTime')}
            </button>
          </div>
        </section>
      )}

      {/* Notifications */}
      <section className="bg-surface p-6 rounded-lg border border-gray-800">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-alert" />
          {t('settings.notificationsTitle')}
        </h2>
        <p className="text-sm text-gray-400 mb-4">
          {t('settings.notificationsDescription')}
        </p>

        <div className="mb-4">
             {hasNotificationAPI ? (
               Notification.permission === 'granted' ? (
                 <div className="text-xs text-green-400">{t('notifications.enabled')}</div>
               ) : (
                 <button
                   onClick={() => Notification.requestPermission()}
                   className="text-xs text-primary underline"
                 >
                   {t('notifications.enable')}
                 </button>
               )
             ) : (
               <div className="text-xs text-gray-500">{t('notifications.unavailable')}</div>
             )}
        </div>

        <div className="grid grid-cols-4 gap-2">
          {notificationOptions.map(mins => (
            <button
              key={mins}
              onClick={() => {
                const newValue = settings.notificationBefore === mins ? 0 : mins;
                updateSettings({ notificationBefore: newValue as 0 | 5 | 10 | 15 | 30 });
              }}
              className={`p-3 rounded-lg border font-bold transition-all ${
                settings.notificationBefore === mins
                  ? 'bg-alert text-white border-alert'
                  : 'bg-background text-gray-400 border-gray-700 hover:border-gray-500'
              }`}
            >
              {t('time.minutesShort', { minutes: mins })}
            </button>
          ))}
        </div>
      </section>

      {/* How to use */}
      <section className="bg-surface p-6 rounded-lg border border-gray-800">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-secondary" />
          {t('settings.howToUseTitle')}
        </h2>
        <p className="text-sm text-gray-400 mb-4">
          {t('settings.howToUseDescription')}
        </p>
        <div className="space-y-3 text-sm text-gray-300">
          <div className="flex items-start gap-2">
            <Wifi className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>{t('settings.useTip1')}</span>
          </div>
          <div className="flex items-start gap-2">
            <Smartphone className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>{t('settings.useTip2')}</span>
          </div>
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>{t('settings.useTip3')}</span>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="bg-surface p-6 rounded-lg border border-gray-800">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-primary" />
          {t('settings.aboutTitle')}
        </h2>
        <p className="text-sm text-gray-400 mb-4">
          {t('settings.aboutParagraph1')}
        </p>
        <p className="text-sm text-gray-400 mb-4">
          {t('settings.aboutParagraph2')}{' '}
          <a href="https://www.buymeacoffee.com/valphusky" target="_blank" rel="noreferrer" className="text-primary underline">
            {t('common.buyMeACoffee')}
          </a>
        </p>
        <p className="text-sm text-gray-400 mb-4">
          {t('settings.aboutParagraph3')}
        </p>

        <div className="space-y-2">
          <a
            href="https://www.buymeacoffee.com/valphusky"
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between rounded-lg border border-gray-700 bg-background px-3 py-2 text-sm text-primary hover:border-gray-500"
          >
            <span className="flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              {t('common.website')}
            </span>
            <span className="text-xs text-gray-500">{t('common.buyMeACoffee')}</span>
          </a>
          <a
            href="https://www.facebook.com/share/16n5Cr2kwT/?mibextid=wwXIfr"
            className="flex items-center justify-between rounded-lg border border-gray-700 bg-background px-3 py-2 text-sm text-primary hover:border-gray-500"
          >
            <span className="flex items-center gap-2">
              <Facebook className="w-4 h-4" />
              {t('common.facebook')}
            </span>
            <span className="text-xs text-gray-500">{t('settings.facebookName')}</span>
          </a>
        </div>

        <div className="text-center text-xs text-gray-600 mt-6">
          v1.0.0 • {t('common.offlineReady')}
        </div>
      </section>
    </div>
  );
};
